<template>
  <v-container fluid>
    <!-- Header -->
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <div>
              <h2>Dashboard</h2>
              <p class="text-subtitle-1 text-grey">Analytics Overview</p>
            </div>
            <v-select
              v-model="selectedYear"
              :items="availableYears"
              label="Select Year"
              variant="outlined"
              density="compact"
              style="max-width: 200px"
            />
          </v-card-title>
        </v-card>
      </v-col>
    </v-row>

    <!-- Stats Cards -->
    <v-row class="mt-4">
      <v-col cols="12" md="6">
        <v-card>
          <v-card-text>
            <div class="d-flex align-center">
              <v-icon size="large" color="primary" class="mr-4">
                mdi-account-group
              </v-icon>
              <div>
                <h3 class="text-h4">{{ selectedYearData.prospects }}</h3>
                <p class="text-subtitle-1">Prospects</p>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" md="6">
        <v-card>
          <v-card-text>
            <div class="d-flex align-center">
              <v-icon size="large" color="success" class="mr-4">
                mdi-account-check
              </v-icon>
              <div>
                <h3 class="text-h4">{{ selectedYearData.customers }}</h3>
                <p class="text-subtitle-1">Customers</p>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts -->
    <v-row class="mt-4">
      <v-col cols="12" lg="8">
        <v-card>
          <v-card-title>Yearly Comparison</v-card-title>
          <v-card-text>
            <div class="chart-container">
              <canvas ref="yearlyChart" width="400" height="200"></canvas>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" lg="4">
        <v-card>
          <v-card-title>Current Year Distribution</v-card-title>
          <v-card-text>
            <div class="chart-container">
              <canvas ref="pieChart" width="300" height="300"></canvas>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Yearly Data Table -->
    <v-row class="mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title>Yearly Data Summary</v-card-title>
          <v-card-text>
            <v-table>
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Prospects</th>
                  <th>Customers</th>
                  <th>Total</th>
                  <th>Conversion Rate</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="year in availableYears" :key="year">
                  <td>{{ year }}</td>
                  <td>{{ dashboardData.prospects[year] }}</td>
                  <td>{{ dashboardData.customers[year] }}</td>
                  <td>{{ dashboardData.prospects[year] + dashboardData.customers[year] }}</td>
                  <td>
                    {{ ((dashboardData.customers[year] / (dashboardData.prospects[year] + dashboardData.customers[year])) * 100).toFixed(1) }}%
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { Chart, registerables } from 'chart.js'

Chart.register(...registerables)

export default {
  name: 'DashboardView',
  data() {
    return {
      selectedYear: new Date().getFullYear(),
      yearlyChart: null,
      pieChart: null
    }
  },
  computed: {
    dashboardData() {
      return this.$store.getters.getDashboardData
    },
    availableYears() {
      return Object.keys(this.dashboardData.prospects).sort()
    },
    selectedYearData() {
      return {
        prospects: this.dashboardData.prospects[this.selectedYear] || 0,
        customers: this.dashboardData.customers[this.selectedYear] || 0
      }
    }
  },
  watch: {
    selectedYear() {
      this.updateCharts()
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initCharts()
    })
  },
  methods: {
    initCharts() {
      try {
        this.createYearlyChart()
        this.createPieChart()
      } catch (error) {
        console.error('Error initializing charts:', error)
      }
    },
    
        createYearlyChart() {
      if (!this.$refs.yearlyChart) {
        console.warn('Yearly chart canvas not found')
        return
      }
      
      try {
        const ctx = this.$refs.yearlyChart.getContext('2d')
        if (!ctx) {
          console.warn('Could not get 2D context for yearly chart')
          return
        }
        
        this.yearlyChart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: this.availableYears,
            datasets: [
              {
                label: 'Prospects',
                data: this.availableYears.map(year => this.dashboardData.prospects[year]),
                backgroundColor: 'rgba(54, 162, 235, 0.8)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1
              },
              {
                label: 'Customers',
                data: this.availableYears.map(year => this.dashboardData.customers[year]),
                backgroundColor: 'rgba(75, 192, 192, 0.8)',
                borderColor: 'rgba(75, 192, 192, 1)',
                borderWidth: 1
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true
              }
            }
          }
        })
      } catch (error) {
        console.error('Error creating yearly chart:', error)
      }
    },
    
    createPieChart() {
      if (!this.$refs.pieChart) {
        console.warn('Pie chart canvas not found')
        return
      }
      
      try {
        const ctx = this.$refs.pieChart.getContext('2d')
        if (!ctx) {
          console.warn('Could not get 2D context for pie chart')
          return
        }
        
        this.pieChart = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: ['Prospects', 'Customers'],
            datasets: [{
              data: [this.selectedYearData.prospects, this.selectedYearData.customers],
              backgroundColor: [
                'rgba(54, 162, 235, 0.8)',
                'rgba(75, 192, 192, 0.8)'
              ],
              borderColor: [
                'rgba(54, 162, 235, 1)',
                'rgba(75, 192, 192, 1)'
              ],
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom'
              }
            }
          }
        })
      } catch (error) {
        console.error('Error creating pie chart:', error)
      }
    },
    
    updateCharts() {
      if (this.yearlyChart) {
        this.yearlyChart.destroy()
      }
      if (this.pieChart) {
        this.pieChart.destroy()
      }
      this.$nextTick(() => {
        this.createYearlyChart()
        this.createPieChart()
      })
    }
  },
  beforeUnmount() {
    if (this.yearlyChart) {
      this.yearlyChart.destroy()
    }
    if (this.pieChart) {
      this.pieChart.destroy()
    }
  }
}
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 400px;
}
</style> 